import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ZehnTopBar, ZehnTopBarItem, useSession } from '@zehnmindai/embed/react';
import { Button } from '@/components/ui/button';
import VersionFooter from '@/components/VersionFooter.jsx';
import { DocsLogo } from '@/shared/ui/DocsLogo.jsx';
import { KbdChip } from '@/shared/components/KbdChip.jsx';
import { openCommandPalette } from '@/shared/utils/commandPalette.js';
import { shortcutLabel } from '@/shared/utils/platform.js';
import { EImzoButton } from '@/shared/ui/EImzoButton.jsx';
import { EImzoSetupModal } from '@/pages/Auth/EImzoRegistration.jsx';
import { iHold } from '@/shared/auth/me.js';
import { SCOPE, isBreakGlassAdmin } from '@/shared/auth/scopes.js';
import { useConsoleAccess } from '@/shared/auth/useScopes.js';

/**
 * docs' header: the install's top bar (@zehnmindai/embed's ZehnTopBar, the
 * same in every module): the logo, the install's modules (tabs, folding into
 * a grid button when they do not fit), search and E-IMZO, and the person menu
 * (language and theme, the person's across the install; the console for
 * whoever administers docs, docs-api's `can_manage`; sign-out through docs'
 * BFF). Navigation inside docs lives in the page below (the location tabs).
 * Framed by the client portal the bar renders nothing: the portal's is the one.
 */
export const TopNavigation = () => {
  const { t } = useTranslation(['common', 'search']);
  const navigate = useNavigate();
  const session = useSession();
  const consoleAccess = useConsoleAccess();
  const [showEImzoModal, setShowEImzoModal] = useState(false);

  const user = session.user;
  // The break-glass account (kind "admin") has no name of its own to show.
  const admin = isBreakGlassAdmin(user);
  const name = user?.displayName || user?.email || (admin ? t('common:userMenu.admin') : '');

  return (
    <>
      <ZehnTopBar
        brand={<DocsLogo />}
        brandHref="/my-docs"
        onBrandClick={() => navigate('/my-docs')}
        actions={
          <div className="flex items-center gap-1 sm:gap-1.5">
            {/* Global search — the palette works everywhere; this teaches it.
                Finding documents is files:read's. */}
            {iHold(SCOPE.FILES_READ) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 px-2"
                onClick={() => openCommandPalette()}
                aria-label={t('search:title')}
                title={t('search:everywhereHint')}
              >
                <Search className="h-4 w-4" />
                <KbdChip className="hidden sm:inline-flex">{shortcutLabel()}</KbdChip>
              </Button>
            )}

            <EImzoButton
              onClick={() => setShowEImzoModal(true)}
              className="h-8"
              labelClassName="hidden sm:inline"
            />
          </div>
        }
        user={{ displayName: name, email: user?.email }}
        onSignOut={() => session.signOut()}
        personDetails={
          <>
            {admin && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {t('common:userMenu.adminHint')}
              </p>
            )}
            <VersionFooter className="mt-2" />
          </>
        }
        menuItems={
          consoleAccess.open && (
            <ZehnTopBarItem icon={<Settings2 />} onClick={() => navigate('/console')}>
              {t('common:userMenu.console')}
            </ZehnTopBarItem>
          )
        }
      />

      <EImzoSetupModal open={showEImzoModal} onOpenChange={setShowEImzoModal} />
    </>
  );
};
